/**
 * Avatar - An atom component for displaying avatars.
 *
 * Displays an avatar image with fallback to an icon.
 * Can be used for users, entities, or any other avatar use case.
 */

import { User, type LucideIcon } from 'lucide-react';
import { cn } from '@lean-agility/design-system/react';

export interface AvatarProps {
  /**
   * Optional image URL for the avatar.
   */
  imageUrl?: string;
  /**
   * Optional icon to use as fallback (defaults to User icon).
   */
  icon?: LucideIcon;
  /**
   * Size of the avatar.
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Alt text for the avatar image.
   */
  alt?: string;
  /**
   * Additional CSS classes.
   */
  className?: string;
}

const sizeClasses = {
  sm: 'h-8 w-8',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
};

const iconSizeClasses = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
};

export function Avatar({ imageUrl, icon: Icon = User, size = 'md', alt = 'Avatar', className }: AvatarProps) {
  return (
    <div
      className={cn(
        `flex shrink-0 items-center justify-center rounded-full bg-action-primary/10 text-action-primary`,
        sizeClasses[size],
        className,
      )}
    >
      {imageUrl ? (
        <img src={imageUrl} alt={alt} className="size-full rounded-full object-cover" />
      ) : (
        <Icon className={iconSizeClasses[size]} />
      )}
    </div>
  );
}
